import { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  ALL_ONBOARDING_FIELDS,
  ONBOARDING_FIELD_LABELS,
  type OnboardingFieldKey,
  type OnboardingFields,
} from '@naya/shared'

// Official "Add to Google Wallet" button (English-US primary variant) from
// Google's brand asset zip (developers.google.com/static/wallet/download-
// assets/add-to-wallet-svg.zip, 2024-09-23 build). Hosted from our own
// origin per the current brand guidelines — the legacy
// gstatic.com/wallet/web/wallet-button/*.svg URLs were retired and now 404.
import addToGoogleWalletButton from './assets/add-to-google-wallet.svg'
// Official "Add to Apple Wallet" badge (US-UK English RGB variant) from Apple's
// Add to Apple Wallet Guidelines artwork package. Apple requires their exact,
// unaltered lockup — do not recolor or resize the SVG's internal geometry.
import addToAppleWalletButton from './assets/add-to-apple-wallet.svg'
import { saveLinkFor, shouldUseApple } from './walletPlatform'
import {
  clearRecovery,
  loadRecovery,
  saveRecovery,
  type StoredRecovery,
} from './passRecovery'

interface EnrollGetResponse {
  tenant: { id: string; name: string; logo_url: string | null }
  branch: { id: string; name: string; slug: string }
  program: {
    id: string
    name: string
    pass_type?: 'loyalty' | 'membership'
    onboarding_fields: OnboardingFields
    owner_terms: string | null
  }
  prefill: Partial<Record<OnboardingFieldKey, string>>
  // SLICE-REFERRAL — true when this program runs a live referral program; gates
  // the referral-code accordion below.
  referral_enabled?: boolean
  // SLICE-MEMBERSHIP-PASS — present only for a self-enroll membership whose
  // program has tiers on. The form then shows a required tier picker.
  membership?: { tiers_enabled: boolean; tiers: string[] } | null
}

interface EnrollPostResponse {
  save_url?: string
  add_to_wallet_url?: string
  card_id?: string
  // Providers the server actually minted a pass for. 'apple' appears only when
  // WALLET_APPLE_ENABLED, so we never show an Apple badge that would dead-end
  // at the "coming soon" page.
  providers_available?: string[]
  // Set when this email already has an active card and the request wasn't
  // staff-mediated. The server returns NO save link/identifiers in this case
  // (it won't hand a stranger someone else's pass); we direct the customer to
  // ask staff to recover their pass instead.
  already_enrolled?: boolean
  // Signed same-phone recovery key for this card — kept in localStorage so
  // this browser can re-add the pass later without staff (see passRecovery.ts).
  recovery_key?: string
}

interface ErrorBody {
  error: { code: string; message?: string; details?: Record<string, unknown> }
}

type FieldState = Partial<Record<OnboardingFieldKey, string>>

const MONTHS: Array<{ value: string; label: string }> = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
]
const DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'))

// Pragmatic email shape check for enabling the submit button — not RFC-complete
// (the server is the authority), just enough to block obvious typos client-side.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function EnrollPage() {
  const { branchSlug } = useParams<{ branchSlug: string }>()
  const [search] = useSearchParams()
  const token = search.get('t') ?? undefined
  // `p` selects which program (multi-program tenants); forward it alongside the
  // prefill token to both the context GET and the enroll POST so the server
  // resolves the exact program the scanned QR encoded.
  const programId = search.get('p') ?? undefined
  // SLICE-REFERRAL — a referral code carried from the /r/<code> Join button.
  const refParam = search.get('ref')?.trim().toUpperCase() || undefined
  const enrollQuery = (() => {
    const params = new URLSearchParams()
    if (token) params.set('t', token)
    if (programId) params.set('p', programId)
    const qs = params.toString()
    return qs ? `?${qs}` : ''
  })()

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [config, setConfig] = useState<EnrollGetResponse | null>(null)
  const [fields, setFields] = useState<FieldState>({})
  const [birthdayMonth, setBirthdayMonth] = useState('')
  const [birthdayDay, setBirthdayDay] = useState('')
  const [consent, setConsent] = useState(false)
  // Honeypot — hidden field a real person never sees or fills. Bots that
  // auto-complete every input set it; the server then silently no-ops the
  // enrollment. Kept in React state so it submits with the rest of the form.
  const [hp, setHp] = useState('')
  // SLICE-REFERRAL — referral code, pre-filled from ?ref= or typed in manually.
  const [referralCode, setReferralCode] = useState(refParam ?? '')
  // SLICE-MEMBERSHIP-PASS — member-chosen tier for a self-enroll tiered membership.
  const [selectedTier, setSelectedTier] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState<EnrollPostResponse | null>(null)
  const [fieldError, setFieldError] = useState<string | null>(null)
  // True right after tapping "Add to Wallet" — the actual save (a top-level
  // navigation/download, not a fetch we can await) can take a couple of
  // seconds while the browser coordinates with Google/Apple Wallet, and a
  // silent gap there reads as "did my tap even register?". Auto-clears so a
  // customer whose wallet hand-off stalls isn't stuck looking at a frozen
  // spinner with no way to retry.
  const [addingToWallet, setAddingToWallet] = useState(false)
  // Same-phone recovery: this browser already enrolled a card for this program
  // (key in localStorage). Shows a "welcome back — add your pass" screen
  // instead of the form, so a cancelled/reloaded add isn't a dead end.
  const [returning, setReturning] = useState<StoredRecovery | null>(null)
  const [recovering, setRecovering] = useState(false)
  // Rolling submit-button text. Enrollment can take a couple of seconds
  // (card insert + Google Wallet pass creation), and a frozen "Loading…"
  // reads as a hang. `submitStep` indexes the message, `submitDots` (0-3)
  // drives the animated trailing ellipsis.
  const [submitStep, setSubmitStep] = useState(0)
  const [submitDots, setSubmitDots] = useState(0)

  useEffect(() => {
    if (!branchSlug) return
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    const url = `/api/public/enroll/${encodeURIComponent(branchSlug)}${enrollQuery}`
    fetch(url, { credentials: 'omit' })
      .then(async (res) => {
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as ErrorBody | null
          throw new Error(
            body?.error?.message ?? `Could not load enrollment (${res.status})`,
          )
        }
        return res.json() as Promise<EnrollGetResponse>
      })
      .then((data) => {
        if (cancelled) return
        setConfig(data)
        setFields(data.prefill)
        // A staff-minted link (?t=) is a deliberate new sign-up at the counter
        // — go straight to the form.
        if (!token) setReturning(loadRecovery(data.program.id))
        const prefilledBirthday = data.prefill.birthday ?? ''
        if (/^\d{2}-\d{2}$/.test(prefilledBirthday)) {
          setBirthdayMonth(prefilledBirthday.slice(0, 2))
          setBirthdayDay(prefilledBirthday.slice(3, 5))
        }
        setLoading(false)
      })
      .catch((err: Error) => {
        if (cancelled) return
        setLoadError(err.message)
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [branchSlug, token, programId])

  // Rolling submit text. Enrollment is slow — the Google Wallet pass build
  // (composited stamp-grid hero image + class/object create) can take ~16s
  // before "Add to Google Wallet" appears. The messages below are ordered
  // to narrate what's actually happening, step by step, so the wait reads
  // as deliberate progress rather than a hang:
  //   save details → create card → build the wallet pass → finishing.
  // At 2.4s each, the eight steps span ~19s and then pin on the last one.
  //   - the DOTS cycle "" → "." → ".." → "..." every 450ms continuously,
  //     so there's visible motion even once the message has settled.
  // SLICE-MEMBERSHIP-PASS — the customer-facing copy adapts to the pass type
  // (a membership has no stamps/rewards). config is null until the GET resolves;
  // by the time these strings render (subtitle, submit, success) it's loaded.
  const isMembership = config?.program.pass_type === 'membership'
  const SUBMIT_MESSAGES: readonly string[] = isMembership
    ? [
        'Saving your details',
        'Creating your membership',
        'Setting up your benefits',
        'Building your wallet pass',
        'Adding your card design',
        'Almost ready',
        'Finishing up',
        'Just a moment',
      ]
    : [
        'Saving your details',
        'Creating your loyalty card',
        'Setting up your stamps',
        'Building your wallet pass',
        'Adding your card design',
        'Almost ready',
        'Finishing up',
        'Just a moment',
      ]
  useEffect(() => {
    if (!submitting) {
      setSubmitStep(0)
      setSubmitDots(0)
      return
    }
    const messageTimer = setInterval(() => {
      setSubmitStep((s) => Math.min(s + 1, SUBMIT_MESSAGES.length - 1))
    }, 2400)
    const dotTimer = setInterval(() => {
      setSubmitDots((d) => (d + 1) % 4)
    }, 450)
    return () => {
      clearInterval(messageTimer)
      clearInterval(dotTimer)
    }
    // SUBMIT_MESSAGES is a stable literal — length never changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitting])

  const enabledFields = useMemo(() => {
    if (!config) return [] as OnboardingFieldKey[]
    return ALL_ONBOARDING_FIELDS.filter(
      (k) => config.program.onboarding_fields[k].enabled,
    ) as OnboardingFieldKey[]
  }, [config])

  // The "Sign up" button is enabled only when the form would actually pass:
  // consent ticked, every REQUIRED field filled, and any email value (required
  // or not) well-formed. Stops the dead-end where consent alone unlocked submit
  // and the server bounced it for missing fields. (birthday is committed to
  // fields.birthday only when both month+day are picked — see commitBirthday.)
  const tierRequired = config?.membership?.tiers_enabled === true
  const formValid = useMemo(() => {
    if (!config || !consent) return false
    if (tierRequired && !selectedTier) return false
    return enabledFields.every((key) => {
      const cfg = config.program.onboarding_fields[key]
      const value = (fields[key] ?? '').trim()
      if (cfg.required && !value) return false
      if (key === 'email' && value && !EMAIL_RE.test(value)) return false
      return true
    })
  }, [config, consent, enabledFields, fields, tierRequired, selectedTier])

  function setField(key: OnboardingFieldKey, value: string) {
    setFields((prev) => ({ ...prev, [key]: value }))
  }

  // Scrolls to + focuses the first thing blocking submission and toasts why,
  // instead of leaving the Sign Up button silently disabled (which reads as
  // "broken" — a customer with no birthday to fill in, say, has no way to
  // know the TOS checkbox is the one thing stopping them).
  function focusFirstInvalid() {
    if (!config) return
    function stop(elementId: string, message: string) {
      const el = document.getElementById(elementId)
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      el?.focus()
      toast.error(message)
    }
    if (tierRequired && !selectedTier) {
      stop('enroll-tier', 'Please choose a membership tier.')
      return
    }
    for (const key of enabledFields) {
      const cfg = config.program.onboarding_fields[key]
      const value = (fields[key] ?? '').trim()
      const elementId = key === 'birthday' ? 'enroll-birthday-month' : `enroll-${key}`
      if (cfg.required && !value) {
        stop(elementId, `Please fill in ${ONBOARDING_FIELD_LABELS[key]}.`)
        return
      }
      if (key === 'email' && value && !EMAIL_RE.test(value)) {
        stop(elementId, 'Please enter a valid email address.')
        return
      }
    }
    if (!consent) {
      stop(
        'enroll-consent-checkbox',
        'Please agree to the Terms of Service and Privacy Policy to continue.',
      )
    }
  }

  // Birthday is stored on the server as a single MM-DD string but rendered
  // as two selects. Track month/day independently so a partial selection
  // (just month picked) is reflected in the UI; only commit to `fields.birthday`
  // when both halves are present.
  function commitBirthday(month: string, day: string) {
    setBirthdayMonth(month)
    setBirthdayDay(day)
    if (month && day) {
      setField('birthday', `${month}-${day}`)
    } else {
      setFields((p) => {
        const next = { ...p }
        delete next.birthday
        return next
      })
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!config) return
    if (!formValid) {
      focusFirstInvalid()
      return
    }
    setSubmitting(true)
    setFieldError(null)
    // A fast enrollment (~300ms) would flash the rolling text and vanish,
    // which reads as a glitch rather than progress. Hold the submitting
    // state for at least MIN_SUBMIT_MS so the customer sees a deliberate
    // "working…" beat. The wait runs only when the request beat the floor.
    const MIN_SUBMIT_MS = 1100
    const startedAt = Date.now()
    const storedKey = loadRecovery(config.program.id)?.key
    try {
      const url = `/api/public/enroll/${encodeURIComponent(branchSlug!)}${enrollQuery}`
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...fields,
          consent: true,
          _hp: hp,
          ...(config.referral_enabled && referralCode.trim()
            ? { referral_code: referralCode.trim() }
            : {}),
          ...(tierRequired && selectedTier
            ? { tier_name: selectedTier }
            : {}),
          // Lets the server re-issue the pass when this email's card is the
          // one this browser enrolled (instead of "already a member").
          ...(storedKey ? { recovery_key: storedKey } : {}),
        }),
      })
      const body = await res.json().catch(() => null)
      if (!res.ok) {
        const err = body as ErrorBody | null
        const code = err?.error?.code ?? 'UNKNOWN'
        const message =
          err?.error?.message ?? 'Could not complete sign-up. Try again.'
        if (code === 'TENANT_SUSPENDED' || code === 'NO_ACTIVE_PROGRAM') {
          setLoadError(message)
        } else {
          setFieldError(message)
        }
        return
      }
      const result = body as EnrollPostResponse
      if (result.recovery_key) {
        saveRecovery(config.program.id, {
          key: result.recovery_key,
          firstName: fields.first_name?.trim() ?? '',
          providersAvailable: result.providers_available ?? ['google'],
        })
      }
      setSuccess(result)
    } catch (err) {
      setFieldError((err as Error).message)
      toast.error('Network error — please try again.')
    } finally {
      const elapsed = Date.now() - startedAt
      if (elapsed < MIN_SUBMIT_MS) {
        await new Promise((r) => setTimeout(r, MIN_SUBMIT_MS - elapsed))
      }
      setSubmitting(false)
    }
  }

  // Trade the stored recovery key for a fresh save link and hand straight off
  // to the wallet. The success screen is set too, so a customer who cancels
  // the wallet sheet again still has a button to tap.
  async function recoverPass() {
    if (!config || !returning || recovering) return
    setRecovering(true)
    try {
      const res = await fetch('/api/public/pass-recovery', {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: returning.key }),
      })
      const body = await res.json().catch(() => null)
      if (!res.ok) {
        if (res.status === 404) {
          // Expired key, or the card is gone — forget it; staff can recover.
          clearRecovery(config.program.id)
          setReturning(null)
          toast.error(
            "We couldn't find your card on this phone. Please ask our staff to recover your pass.",
          )
        } else {
          toast.error(
            (body as ErrorBody | null)?.error?.message ??
              'Could not load your pass. Try again.',
          )
        }
        return
      }
      const result = body as EnrollPostResponse
      if (!fields.first_name && returning.firstName) {
        setField('first_name', returning.firstName)
      }
      setSuccess(result)
      setReturning(null)
      const href = saveLinkFor(
        result.add_to_wallet_url,
        shouldUseApple(result.providers_available),
      )
      if (href) window.location.assign(href)
    } catch {
      toast.error('Network error — please try again.')
    } finally {
      setRecovering(false)
    }
  }

  if (loading) {
    return <div className="enroll-status">Loading…</div>
  }
  if (loadError || !config) {
    return (
      <div className="enroll-screen">
        <div className="enroll-card">
          <div className="enroll-header">
            <h1 className="enroll-title">Sign-up unavailable</h1>
            <p className="enroll-subtitle">
              {loadError ?? 'Please try again later.'}
            </p>
          </div>
        </div>
      </div>
    )
  }
  if (returning && !success) {
    const useApple = shouldUseApple(returning.providersAvailable)
    const greeting = returning.firstName
      ? `Welcome back, ${returning.firstName}!`
      : 'Welcome back!'
    return (
      <div className="enroll-screen">
        <div className="enroll-card enroll-card-success">
          <div className="enroll-success">
            {config.tenant.logo_url && (
              <img
                src={config.tenant.logo_url}
                alt={config.tenant.name}
                className="enroll-success-logo"
              />
            )}
            <h2>{greeting}</h2>
            <p>
              You already signed up at <strong>{config.tenant.name}</strong> on
              this phone. Tap below to add your{' '}
              {isMembership ? 'membership pass' : 'loyalty card'} to your
              wallet.
            </p>
            <button
              type="button"
              className={`enroll-wallet-button${recovering ? ' is-loading' : ''}`}
              aria-label={useApple ? 'Add to Apple Wallet' : 'Add to Google Wallet'}
              aria-busy={recovering}
              onClick={() => void recoverPass()}
            >
              {recovering ? (
                <span className="enroll-wallet-loading">
                  <span className="enroll-wallet-spinner" aria-hidden="true" />
                  Preparing your pass…
                </span>
              ) : (
                <img
                  src={useApple ? addToAppleWalletButton : addToGoogleWalletButton}
                  alt={useApple ? 'Add to Apple Wallet' : 'Add to Google Wallet'}
                />
              )}
            </button>
            <button
              type="button"
              className="enroll-text-button"
              onClick={() => setReturning(null)}
            >
              Not you? Sign up someone else
            </button>
          </div>
        </div>
      </div>
    )
  }
  if (success?.already_enrolled) {
    // Returning customer enrolling cold (no staff) from a browser that does
    // NOT hold this card's recovery key (a matching key gets the pass
    // re-issued server-side instead). We don't self-serve a re-add by email
    // alone — they recover their pass through staff. Show a clear, non-leaky
    // message (no wallet button, no card data).
    return (
      <div className="enroll-screen">
        <div className="enroll-card enroll-card-success">
          <div className="enroll-success">
            {config.tenant.logo_url && (
              <img
                src={config.tenant.logo_url}
                alt={config.tenant.name}
                className="enroll-success-logo"
              />
            )}
            <h2>You're already a member!</h2>
            <p>
              This email is already signed up at{' '}
              <strong>{config.tenant.name}</strong>. To add your{' '}
              {isMembership ? 'membership pass' : 'loyalty card'} to this phone,
              just ask our staff to recover your pass for you.
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (success) {
    const firstName = fields.first_name?.trim() ?? ''
    // Personalize when we collected a name; otherwise the bare "You're in!"
    // reads cleaner than "Hi! You're in!".
    const greeting = firstName ? `Hi ${firstName}, you're in!` : "You're in!"
    // The badge matches the device, and the /save link carries the same pick
    // as a `wallet` hint so the server serves the wallet the badge promised.
    const ua = navigator.userAgent
    const useApple = shouldUseApple(success.providers_available)
    // Enroll links commonly arrive via Messenger/Facebook/Instagram in PH, whose
    // in-app browsers frequently fail to hand a pass to Wallet. Detect those
    // webviews and nudge the customer to open in the system browser.
    const isInAppBrowser =
      /FBAN|FBAV|FB_IAB|Instagram|Line\/|Messenger|MicroMessenger|; wv\)/i.test(
        ua,
      )
    const openInBrowserHint = useApple
      ? 'Tap the ••• menu and choose "Open in Safari" so the pass can be added.'
      : 'Tap the ••• menu and choose "Open in browser" so the pass can be added.'
    return (
      <div className="enroll-screen">
        <div className="enroll-card enroll-card-success">
          <div className="enroll-success">
            {config.tenant.logo_url && (
              <img
                src={config.tenant.logo_url}
                alt={config.tenant.name}
                className="enroll-success-logo"
              />
            )}
            <h2>{greeting}</h2>
            <p>
              {isMembership ? (
                <>
                  Add your membership pass to your wallet to use it at{' '}
                  <strong>{config.tenant.name}</strong>.
                </>
              ) : (
                <>
                  Add your loyalty card to your wallet to start earning stamps
                  at <strong>{config.tenant.name}</strong>.
                </>
              )}
            </p>
            <a
              href={saveLinkFor(success.add_to_wallet_url, useApple)}
              className={`enroll-wallet-button${addingToWallet ? ' is-loading' : ''}`}
              aria-label={useApple ? 'Add to Apple Wallet' : 'Add to Google Wallet'}
              aria-busy={addingToWallet}
              onClick={(e) => {
                if (addingToWallet) {
                  // Already in flight — a fast double-tap shouldn't fire a
                  // second navigation/download.
                  e.preventDefault()
                  return
                }
                setAddingToWallet(true)
                window.setTimeout(() => setAddingToWallet(false), 5000)
                // Not preventDefault()'d — the link's normal navigation (302
                // to Google, or the .pkpass download handoff to Apple
                // Wallet) still proceeds; this just shows feedback while
                // the browser handles it.
              }}
            >
              {addingToWallet ? (
                <span className="enroll-wallet-loading">
                  <span className="enroll-wallet-spinner" aria-hidden="true" />
                  Preparing your pass…
                </span>
              ) : (
                <img
                  src={useApple ? addToAppleWalletButton : addToGoogleWalletButton}
                  alt={useApple ? 'Add to Apple Wallet' : 'Add to Google Wallet'}
                />
              )}
            </a>
            {isInAppBrowser && (
              <p className="enroll-inapp-hint">{openInBrowserHint}</p>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="enroll-screen">
      <div className="enroll-card">
        <div className="enroll-header">
          {config.tenant.logo_url && (
            <img
              src={config.tenant.logo_url}
              alt={config.tenant.name}
              className="enroll-logo"
            />
          )}
          <h1 className="enroll-title">{config.tenant.name}</h1>
          <p className="enroll-subtitle">
            Sign up for the {config.program.name}{' '}
            {config.program.pass_type === 'membership'
              ? 'membership'
              : 'loyalty program'}{' '}
            at {config.branch.name}.
          </p>
        </div>
      <form className="enroll-form" onSubmit={onSubmit} noValidate>
        {/*
          Honeypot. Off-screen, not announced to screen readers, skipped by
          keyboard tab order, autocomplete disabled — a real person never
          touches it. Bots that fill every field set it and get silently
          no-op'd server-side. Not display:none (some bots skip hidden inputs).
        */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '-9999px',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
          }}
        >
          <label htmlFor="enroll-company-website">Do not fill this field</label>
          <input
            id="enroll-company-website"
            type="text"
            name="company_website"
            tabIndex={-1}
            autoComplete="off"
            value={hp}
            onChange={(e) => setHp(e.target.value)}
          />
        </div>
        {tierRequired && config.membership && (
          <div className="enroll-field">
            <label htmlFor="enroll-tier">
              Membership tier <span className="enroll-required">*</span>
            </label>
            <select
              id="enroll-tier"
              value={selectedTier}
              onChange={(e) => setSelectedTier(e.target.value)}
            >
              <option value="">Choose a tier…</option>
              {config.membership.tiers.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        )}
        {enabledFields.map((key) => {
          const cfg = config.program.onboarding_fields[key]
          const label = ONBOARDING_FIELD_LABELS[key]
          if (key === 'birthday') {
            return (
              <div key={key} className="enroll-field">
                <label htmlFor="enroll-birthday-month">
                  {label}
                  {cfg.required && <span className="enroll-required">*</span>}
                </label>
                <div className="enroll-birthday">
                  <select
                    id="enroll-birthday-month"
                    className="enroll-select"
                    value={birthdayMonth}
                    onChange={(e) => commitBirthday(e.target.value, birthdayDay)}
                    required={cfg.required}
                  >
                    <option value="">Month</option>
                    {MONTHS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                  <select
                    className="enroll-select"
                    value={birthdayDay}
                    onChange={(e) => commitBirthday(birthdayMonth, e.target.value)}
                    required={cfg.required}
                    aria-label="Day"
                  >
                    <option value="">Day</option>
                    {DAYS.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
              </div>
            )
          }
          const inputType =
            key === 'email' ? 'email' : key === 'phone' ? 'tel' : 'text'
          return (
            <div key={key} className="enroll-field">
              <label htmlFor={`enroll-${key}`}>
                {label}
                {cfg.required && <span className="enroll-required">*</span>}
              </label>
              <input
                id={`enroll-${key}`}
                type={inputType}
                className="enroll-input"
                value={fields[key] ?? ''}
                onChange={(e) => setField(key, e.target.value)}
                required={cfg.required}
                autoComplete={key === 'first_name' ? 'given-name' : key === 'last_name' ? 'family-name' : key}
                // Email is normalised to lower-case server-side; stop the
                // mobile keyboard auto-capitalising the first letter so the
                // customer doesn't see a casing they didn't type.
                autoCapitalize={key === 'email' ? 'none' : 'words'}
                autoCorrect={key === 'email' ? 'off' : undefined}
                spellCheck={key === 'email' ? false : undefined}
              />
            </div>
          )
        })}

        {config.referral_enabled &&
          (refParam ? (
            // Arrived from an invite link — show a compact confirmation instead
            // of an open accordion. The code already lives in `referralCode`
            // state and submits regardless of a visible input.
            <div className="enroll-referral-applied">
              <span className="enroll-referral-applied-icon" aria-hidden="true">
                ✓
              </span>
              <span>
                Invite applied — code <strong>{referralCode}</strong>. You’ll
                earn your welcome bonus on your first stamp.
              </span>
            </div>
          ) : (
            <details className="enroll-referral">
              <summary>Have a referral code?</summary>
              <input
                type="text"
                className="enroll-input"
                name="referral_code"
                autoComplete="off"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                placeholder="Enter a friend’s code"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              />
            </details>
          ))}

        <label className="enroll-consent">
          <input
            id="enroll-consent-checkbox"
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
          />
          <span>
            I agree to Naya Pass's{' '}
            <a
              href="https://pass.naya.ph/terms"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="https://pass.naya.ph/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            , and consent to <strong>{config.tenant.name}</strong> collecting
            my information for {isMembership ? 'my membership' : 'loyalty rewards'}
            .
          </span>
        </label>

        {fieldError && <p className="enroll-error">{fieldError}</p>}

        <button
          type="submit"
          className="enroll-submit"
          disabled={submitting}
        >
          {submitting ? (
            <>
              {SUBMIT_MESSAGES[submitStep]}
              {/* Fixed-width dots so the label doesn't shift as they
                  animate — the span always reserves 3 chars. */}
              <span className="enroll-submit-dots" aria-hidden="true">
                {'.'.repeat(submitDots)}
              </span>
            </>
          ) : (
            'Sign up'
          )}
        </button>
      </form>
      </div>
    </div>
  )
}
